import * as React from "react"
import { Head, useForm, usePage } from "@inertiajs/react"
import { ExternalLink } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { AdminPageHeader, AdminSection } from "@/components/admin/AdminPageHeader"
import { CheckboxRow, Field, fieldAria } from "@/components/admin/Field"
import { ErrorSummary, ImagePicker, FormActions } from "@/components/admin/FormParts"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import type { AdminWineFormProps, PageProps } from "@/types/inertia"

type WineFormData = {
  name: string
  producer: string
  wine_type: string
  taste_profile: string
  description: string
  tasting_notes: string
  food_pairing: string
  price_pounds: string
  sideways_url: string
  carried: boolean
  featured: boolean
  image: File | null
}

export default function AdminWineForm() {
  const { props } = usePage<PageProps<AdminWineFormProps>>()
  const { wine, wine_types: wineTypes, taste_profiles: tasteProfiles, submit_url: submitUrl, submit_method: submitMethod, back_url: backUrl } = props
  const editing = wine.id !== null
  const title = editing ? wine.name : "Add a wine"
  const description = editing
    ? `Edit ${wine.name}: its copy, UK price, carried and featured flags, image and Sideways link.`
    : "Add a wine by hand: its copy, UK price, carried and featured flags, image and Sideways link."

  const form = useForm<WineFormData>({
    name: wine.name,
    producer: wine.producer,
    wine_type: wine.wine_type,
    taste_profile: wine.taste_profile,
    description: wine.description,
    tasting_notes: wine.tasting_notes,
    food_pairing: wine.food_pairing,
    price_pounds: wine.price_pounds,
    sideways_url: wine.sideways_url,
    carried: wine.carried,
    featured: wine.featured,
    image: null,
  })
  const errors = form.errors as Partial<Record<keyof WineFormData, string>>

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    // Multipart for the image; an edit spoofs PATCH through POST (W1 contract).
    form.transform((data) => (submitMethod === "patch" ? { ...data, _method: "patch" } : data))
    // A save re-renders from the database; a failed one keeps what was typed.
    form.post(submitUrl, { forceFormData: true, preserveState: "errors" })
  }

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      <AdminShell>
        <AdminPageHeader
          title={title}
          description={editing ? "Every column of this wine. The UK price is entered in pounds." : "Every column of a new wine. The UK price is entered in pounds."}
          back={{ href: backUrl, label: "All wines" }}
          actions={
            wine.public_url && (
              <Button asChild variant="secondary" size="lg">
                <a href={wine.public_url} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-4 w-4" aria-hidden="true" />
                  View on the site
                </a>
              </Button>
            )
          }
        />

        <form onSubmit={submit} className="max-w-2xl" noValidate>
          <ErrorSummary errors={errors} />

          <AdminSection id="wine-identity" title="The wine">
            <div className="space-y-4">
              <Field id="wine-name" label="Name" error={errors.name} hint="The Sideways name as imported; the site tidies it for display.">
                <Input
                  {...fieldAria("wine-name", { error: errors.name, hint: true })}
                  className="form-control-large"
                  value={form.data.name}
                  onChange={(event) => form.setData("name", event.target.value)}
                  required
                />
              </Field>
              <Field id="wine-producer" label="Producer" error={errors.producer}>
                <Input
                  {...fieldAria("wine-producer", { error: errors.producer })}
                  className="form-control-large"
                  value={form.data.producer}
                  onChange={(event) => form.setData("producer", event.target.value)}
                  required
                />
              </Field>
              <Field id="wine-type" label="Type" error={errors.wine_type}>
                <Select
                  {...fieldAria("wine-type", { error: errors.wine_type })}
                  className="form-control-large"
                  value={form.data.wine_type}
                  onChange={(event) => form.setData("wine_type", event.target.value)}
                  required
                >
                  <option value="" disabled>
                    Choose a type…
                  </option>
                  {wineTypes.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field id="wine-taste" label="Taste" error={errors.taste_profile} hint="Used by the wine finder. Leave as None if it is not set yet.">
                <Select
                  {...fieldAria("wine-taste", { error: errors.taste_profile, hint: true })}
                  className="form-control-large"
                  value={form.data.taste_profile}
                  onChange={(event) => form.setData("taste_profile", event.target.value)}
                >
                  <option value="">None</option>
                  {tasteProfiles.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
          </AdminSection>

          <AdminSection id="wine-price" title="Price and availability">
            <div className="space-y-4">
              <Field
                id="wine-price-pounds"
                label="UK price per bottle (£)"
                error={errors.price_pounds}
                hint="In pounds, such as 14.50. Leave empty for “Price on request”."
              >
                <Input
                  {...fieldAria("wine-price-pounds", { error: errors.price_pounds, hint: true })}
                  className="form-control-large"
                  inputMode="decimal"
                  autoComplete="off"
                  value={form.data.price_pounds}
                  onChange={(event) => form.setData("price_pounds", event.target.value)}
                />
              </Field>
              <div>
                <CheckboxRow
                  label="Carried"
                  description="Listed on the Our Wines page and in the wine finder."
                  control={
                    <Checkbox
                      checked={form.data.carried}
                      onChange={(event) => form.setData("carried", event.target.checked)}
                    />
                  }
                />
                <CheckboxRow
                  label="Featured"
                  description="Shown on the home page once it is carried."
                  control={
                    <Checkbox
                      checked={form.data.featured}
                      onChange={(event) => form.setData("featured", event.target.checked)}
                    />
                  }
                />
              </div>
            </div>
          </AdminSection>

          <AdminSection id="wine-copy" title="Copy">
            <div className="space-y-4">
              <Field id="wine-description" label="Description" error={errors.description}>
                <Textarea
                  {...fieldAria("wine-description", { error: errors.description })}
                  className="form-control-large"
                  rows={5}
                  value={form.data.description}
                  onChange={(event) => form.setData("description", event.target.value)}
                />
              </Field>
              <Field id="wine-tasting-notes" label="Tasting notes" error={errors.tasting_notes}>
                <Textarea
                  {...fieldAria("wine-tasting-notes", { error: errors.tasting_notes })}
                  className="form-control-large"
                  rows={4}
                  value={form.data.tasting_notes}
                  onChange={(event) => form.setData("tasting_notes", event.target.value)}
                />
              </Field>
              <Field id="wine-food-pairing" label="Food pairing" error={errors.food_pairing}>
                <Textarea
                  {...fieldAria("wine-food-pairing", { error: errors.food_pairing })}
                  className="form-control-large"
                  rows={3}
                  value={form.data.food_pairing}
                  onChange={(event) => form.setData("food_pairing", event.target.value)}
                />
              </Field>
            </div>
          </AdminSection>

          <AdminSection id="wine-image" title="Image">
            <ImagePicker
              id="wine-image-file"
              label={wine.image_url ? "Replace the image" : "Upload an image"}
              hint="PNG, JPEG or WebP, up to 10 MB. A bottle shot on a plain background works best."
              currentUrl={wine.image_url}
              currentAlt={`Current image of ${wine.name}`}
              file={form.data.image}
              error={errors.image}
              onChange={(file) => form.setData("image", file)}
            />
          </AdminSection>

          <AdminSection id="wine-source" title="Source">
            <Field
              id="wine-sideways-url"
              label="Sideways link"
              error={errors.sideways_url}
              hint="The wine's page on the Sideways site, for reference."
            >
              <Input
                {...fieldAria("wine-sideways-url", { error: errors.sideways_url, hint: true })}
                type="url"
                inputMode="url"
                className="form-control-large"
                value={form.data.sideways_url}
                onChange={(event) => form.setData("sideways_url", event.target.value)}
              />
            </Field>
          </AdminSection>

          <FormActions
            processing={form.processing}
            submitLabel={editing ? "Save changes" : "Add wine"}
            cancelHref={backUrl}
          />
        </form>
      </AdminShell>
    </>
  )
}
